// frontend/src/modules/client/ProfileModule.jsx
// ═══════════════════════════════════════════════════════════════════════════
// PROFIL CLIENT AVEC GESTION PANIER + CRÉNEAU RETRAIT PRÉFÉRÉ + MOT DE PASSE
// ═══════════════════════════════════════════════════════════════════════════
// Papy 73+ (Marine marchande, Normandie)
// Permet de voir, modifier, supprimer les réservations en cours
// + Choix du créneau de retrait préféré (vendredi/samedi)
// + Modification mot de passe
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { 
  User, ArrowLeft, ShoppingBag, Calendar, Clock, Euro, 
  ChevronDown, ChevronUp, Edit, Package, Trash2, Plus, Minus,
  Send, AlertCircle, CheckCircle, RefreshCw, MapPin, Save,
  Lock, Eye, EyeOff
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useMessageBox } from '../../components/MessageBox';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const ProfileModule = ({ user, setUser, config, apiBase, setCart, periodeActive }) => {
  const navigate = useNavigate();
  // ✅ Hook MessageBox
  const { showMessage, showConfirm, confirmDelete } = useMessageBox();
  
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedResa, setExpandedResa] = useState(null);
  const [lignesResa, setLignesResa] = useState({});
  const [editingResa, setEditingResa] = useState(null);
  const [editingLignes, setEditingLignes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // États pour les créneaux de retrait
  // ═══════════════════════════════════════════════════════════════════════════
  const [creneaux, setCreneaux] = useState([]);
  const [creneauPrefere, setCreneauPrefere] = useState(user?.creneau_retrait_prefere || '');
  const [savingCreneau, setSavingCreneau] = useState(false);
  const [creneauMessage, setCreneauMessage] = useState(null);

  // ═══════════════════════════════════════════════════════════════════════════
  // États pour modification mot de passe
  // ═══════════════════════════════════════════════════════════════════════════
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);

  // Charger les réservations du client
  useEffect(() => {
    if (user?.code_client) {
      loadReservations();
      loadCreneaux();
    }
  }, [user]);

  // Synchroniser créneau préféré quand user change
  useEffect(() => {
    console.log('🔍 DEBUG user.creneau_retrait_prefere:', user?.creneau_retrait_prefere);
    if (user?.creneau_retrait_prefere) {
      setCreneauPrefere(user.creneau_retrait_prefere);
    }
  }, [user?.creneau_retrait_prefere]);

  const loadReservations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiBase || API_BASE}/reservations/client/${user.code_client}`);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setReservations(result.data || []);
          
          // Auto-expand la première réservation en cours
          const enCours = (result.data || []).find(r => 
            ['en_cours', 'confirmee'].includes(r.statut)
          );
          if (enCours) {
            setExpandedResa(enCours.id);
            loadLignesReservation(enCours.id);
          }
        }
      }
    } catch (error) {
      console.error('Erreur chargement réservations:', error);
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // Charger les créneaux de retrait disponibles
  // ═══════════════════════════════════════════════════════════════════════════
  const loadCreneaux = async () => {
    try {
      const response = await fetch(`${apiBase || API_BASE}/creneaux-retrait/actifs`);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setCreneaux(result.data || []);
        }
      }
    } catch (error) {
      console.error('Erreur chargement créneaux:', error);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // Sauvegarder le créneau préféré
  // ═══════════════════════════════════════════════════════════════════════════
  const saveCreneauPrefere = async () => {
    if (!creneauPrefere) {
      setCreneauMessage({ type: 'error', text: 'Veuillez sélectionner un créneau' });
      return;
    }

    setSavingCreneau(true);
    setCreneauMessage(null);

    try {
      const response = await fetch(`${apiBase || API_BASE}/creneaux-retrait/user/${user.code_client}/prefere`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ creneau_retrait_prefere: creneauPrefere })
      });

      const result = await response.json();

      if (result.success) {
        setCreneauMessage({ type: 'success', text: '✅ Créneau préféré enregistré !' });
        
        // Recharger les données user depuis la BDD
        try {
          const userResponse = await fetch(`${apiBase || API_BASE}/clients/code/${user.code_client}`);
          const userResult = await userResponse.json();
          
          if (userResult.success && userResult.data) {
            console.log('DEBUG données rechargées:', userResult.data);
            console.log('DEBUG creneau dans data:', userResult.data.creneau_retrait_prefere);
            // Mettre à jour le user avec les données fraîches de la BDD
            if (setUser) {
              setUser(userResult.data);
            }
          }
        } catch (reloadError) {
          console.warn('Impossible de recharger le user:', reloadError);
          // Fallback : mise à jour locale
          if (setUser) {
            setUser(prev => ({ ...prev, creneau_retrait_prefere: creneauPrefere }));
          }
        }
        
        setTimeout(() => setCreneauMessage(null), 3000);
      } else {
        throw new Error(result.message || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur sauvegarde créneau:', error);
      setCreneauMessage({ type: 'error', text: `❌ ${error.message}` });
    } finally {
      setSavingCreneau(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // Modifier le mot de passe
  // ═══════════════════════════════════════════════════════════════════════════
  const handleChangePassword = async () => {
    // Validation
    if (!passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'Veuillez remplir tous les champs' });
      return;
    }

    if (passwordData.newPassword.length < 4) {
      setPasswordMessage({ type: 'error', text: 'Le mot de passe doit faire au moins 4 caractères' });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'Les mots de passe ne correspondent pas' });
      return;
    }

    setSavingPassword(true);
    setPasswordMessage(null);

    try {
      const response = await fetch(`${apiBase || API_BASE}/clients/${user.code_client}/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          newPassword: passwordData.newPassword 
        })
      });

      const result = await response.json();

      if (result.success) {
        setPasswordMessage({ type: 'success', text: '✅ Mot de passe modifié avec succès !' });
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowPasswordSection(false);
        setTimeout(() => setPasswordMessage(null), 5000);
      } else {
        throw new Error(result.message || 'Erreur lors de la modification');
      }
    } catch (error) {
      console.error('Erreur modification mot de passe:', error);
      setPasswordMessage({ type: 'error', text: `❌ ${error.message}` });
    } finally {
      setSavingPassword(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  // Charger les lignes d'une réservation
  const loadLignesReservation = async (reservationId) => {
    try {
      const response = await fetch(`${apiBase || API_BASE}/reservations/${reservationId}/lignes`);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setLignesResa(prev => ({
            ...prev,
            [reservationId]: result.data || []
          }));
        }
      }
    } catch (error) {
      console.error('Erreur chargement lignes:', error);
    }
  };

  // Toggle détails réservation
  const toggleDetails = (resaId) => {
    if (expandedResa === resaId) {
      setExpandedResa(null);
      setEditingResa(null);
    } else {
      setExpandedResa(resaId);
      if (!lignesResa[resaId]) {
        loadLignesReservation(resaId);
      }
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // MODE ÉDITION
  // ═══════════════════════════════════════════════════════════════════════════
  const startEditing = (resa) => {
    setEditingResa(resa.id);
    setEditingLignes([...(lignesResa[resa.id] || [])]);
  };

  const cancelEditing = () => {
    setEditingResa(null);
    setEditingLignes([]);
  };

  const updateQuantite = (ligneId, delta) => {
    setEditingLignes(prev => prev.map(ligne => {
      if (ligne.id === ligneId) {
        const newQte = Math.max(0, parseFloat(ligne.quantite_reservee) + delta);
        return {
          ...ligne,
          quantite_reservee: newQte,
          montant_estime: newQte * parseFloat(ligne.prix_unitaire)
        };
      }
      return ligne;
    }));
  };

  const removeLigne = (ligneId) => {
    setEditingLignes(prev => prev.filter(ligne => ligne.id !== ligneId));
  };

  const calculateTotal = () => {
    return editingLignes.reduce((sum, ligne) => 
      sum + (parseFloat(ligne.quantite_reservee) * parseFloat(ligne.prix_unitaire)), 0
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // SAUVEGARDER LES MODIFICATIONS
  // ═══════════════════════════════════════════════════════════════════════════
  const saveModifications = async () => {
    if (editingLignes.length === 0) {
      const ok = await showConfirm('⚠️ Votre panier est vide.\n\nVoulez-vous annuler cette réservation ?', 'warning');
      if (!ok) return;
    }

    setSaving(true);
    setMessage(null);

    try {
      const response = await fetch(`${apiBase || API_BASE}/reservations/${editingResa}/lignes/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lignes: editingLignes.filter(l => l.quantite_reservee > 0).map(l => ({
            code_produit: l.code_produit,
            designation_produit: l.designation_produit,
            unite: l.unite,
            quantite_reservee: l.quantite_reservee,
            prix_unitaire: l.prix_unitaire
          }))
        })
      });

      const result = await response.json();

      if (result.success) {
        setMessage({
          type: 'success',
          text: `✅ Modifications enregistrées ! Nouveau total: ${result.montant_total.toFixed(2)}€`
        });
        
        // Recharger les données
        await loadReservations();
        await loadLignesReservation(editingResa);
        setEditingResa(null);
        setEditingLignes([]);
        
        setTimeout(() => setMessage(null), 5000);
      } else {
        throw new Error(result.message || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      setMessage({
        type: 'error',
        text: `❌ Erreur: ${error.message}`
      });
    } finally {
      setSaving(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ANNULER UNE RÉSERVATION
  // ═══════════════════════════════════════════════════════════════════════════
  const annulerReservation = async (resaId, numeroResa) => {
    const ok = await confirmDelete(`Annuler la réservation ${numeroResa} ?\n\nCette action est irréversible.`);
    if (!ok) return;

    setSaving(true);
    try {
      const response = await fetch(`${apiBase || API_BASE}/reservations/${resaId}`, {
        method: 'DELETE'
      });

      const result = await response.json();

      if (result.success) {
        setMessage({
          type: 'success',
          text: '✅ Réservation annulée'
        });
        await loadReservations();
        setTimeout(() => setMessage(null), 3000);
      } else {
        throw new Error(result.message || 'Erreur');
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `❌ Erreur: ${error.message}`
      });
    } finally {
      setSaving(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // CONTINUER AU CATALOGUE (pour ajouter des produits)
  // ═══════════════════════════════════════════════════════════════════════════
  const continuerAuCatalogue = async (resaId) => {
    // Charger les lignes de la réservation dans le panier AVANT de naviguer
    try {
      const response = await fetch(`${apiBase || API_BASE}/reservations/${resaId}/lignes`);
      const result = await response.json();
      
      if (result.success && result.data.length > 0) {
        // Convertir les lignes en format panier
        const cartFromResa = result.data.map(ligne => ({
          id: ligne.id,
          code_produit: ligne.code_produit,
          designation: ligne.designation_produit,
          prix_ttc: parseFloat(ligne.prix_unitaire),
          unite: ligne.unite,
          quantite: parseFloat(ligne.quantite_reservee),
          image_lien: ligne.image_lien,
          quantite_disponible: 999 // Sera mis à jour par le catalogue
        }));
        
        // Mettre à jour le panier global
        setCart(cartFromResa);
        console.log('✅ Panier pré-chargé avec', cartFromResa.length, 'produit(s)');
      }
      
      // Naviguer vers le catalogue SANS paramètre edit (le panier est déjà chargé)
      navigate('/catalog');
      
    } catch (error) {
      console.error('Erreur chargement panier:', error);
      // En cas d'erreur, naviguer quand même
      navigate('/catalog');
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RETOUR AU CATALOGUE (simple, sans charger la résa)
  // ═══════════════════════════════════════════════════════════════════════════
  const retourCatalogue = () => {
    navigate('/catalog');
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // FORMATAGE DATES
  // ═══════════════════════════════════════════════════════════════════════════
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short'
    });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // STYLE DES STATUTS
  // ═══════════════════════════════════════════════════════════════════════════
  const getStatutStyle = (statut) => {
    const styles = {
      'en_cours': { bg: 'bg-orange-100', text: 'text-orange-800', label: '📝 Demande transmise', description: 'En attente de confirmation du fermier' },
      'confirmee': { bg: 'bg-green-100', text: 'text-green-800', label: '✅ Confirmée', description: 'Le fermier a validé votre réservation' },
      'en_recolte': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: '🌱 En récolte', description: 'Vos produits sont en cours de récolte' },
      'en_preparation': { bg: 'bg-blue-100', text: 'text-blue-800', label: '📦 En préparation', description: 'Votre commande est en cours de préparation' },
      'prete': { bg: 'bg-purple-100', text: 'text-purple-800', label: '🎉 Prête', description: 'Votre commande est prête à être retirée !' },
      'retiree': { bg: 'bg-gray-100', text: 'text-gray-800', label: '✔️ Retirée', description: 'Merci de votre visite' },
      'annulee': { bg: 'bg-red-100', text: 'text-red-800', label: '❌ Annulée', description: 'Cette réservation a été annulée' }
    };
    return styles[statut] || { bg: 'bg-gray-100', text: 'text-gray-600', label: statut, description: '' };
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // PROTECTION SI USER NON CHARGÉ
  // ═══════════════════════════════════════════════════════════════════════════
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement du profil...</p>
        </div>
      </div>
    );
  }

  // Séparer réservations en cours vs passées
  const resasEnCours = reservations.filter(r => 
    ['en_cours', 'confirmee', 'en_recolte', 'en_preparation', 'prete'].includes(r.statut)
  );
  const resasPassees = reservations.filter(r => 
    ['retiree', 'annulee'].includes(r.statut)
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDU JSX
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/catalog')}
              className="text-green-600 hover:text-green-800"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-xl font-bold text-green-800">Mon Profil</h1>
          </div>
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        
        {/* Message global */}
        {message && (
          <div className={`p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' 
              ? 'bg-green-100 text-green-800 border border-green-300' 
              : 'bg-red-100 text-red-800 border border-red-300'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="h-5 w-5" />
            ) : (
              <AlertCircle className="h-5 w-5" />
            )}
            <span className="font-medium">{message.text}</span>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* CARTE PROFIL AVEC CRÉNEAU PRÉFÉRÉ */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-green-100 p-3 rounded-full">
              <User className="h-8 w-8 text-green-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{user.nom_prenom}</h2>
              <p className="text-gray-600">Code client: {user.code_client}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="text-sm font-medium text-gray-500">📧 Email</label>
              <p className="text-gray-900 font-medium">{user.email || 'Non renseigné'}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="text-sm font-medium text-gray-500">📞 Téléphone</label>
              <p className="text-gray-900 font-medium">{user.telephone || 'Non renseigné'}</p>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════════════ */}
          {/* SÉLECTION CRÉNEAU DE RETRAIT PRÉFÉRÉ */}
          {/* ═══════════════════════════════════════════════════════════════════ */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <MapPin className="h-5 w-5 text-green-600" />
              Créneau de retrait préféré
            </h3>
            
            <p className="text-sm text-gray-600 mb-4">
              Choisissez votre créneau de retrait habituel. Ce choix sera pré-sélectionné lors de vos prochaines réservations.
            </p>

            {creneaux.length === 0 ? (
              <div className="bg-gray-50 p-4 rounded-lg text-gray-500 text-sm">
                <RefreshCw className="h-4 w-4 inline mr-2 animate-spin" />
                Chargement des créneaux...
              </div>
            ) : (
              <>
                <div className="space-y-3 mb-4">
                  {creneaux.map(creneau => (
                    <label 
                      key={creneau.id}
                      className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        creneauPrefere === creneau.code_creneau
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 bg-white hover:border-green-300 hover:bg-green-50/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="creneau_prefere"
                        value={creneau.code_creneau}
                        checked={creneauPrefere === creneau.code_creneau}
                        onChange={(e) => setCreneauPrefere(e.target.value)}
                        className="h-5 w-5 text-green-600 focus:ring-green-500"
                      />
                      <div className="ml-4 flex-1">
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-gray-900">
                            {creneau.libelle}
                          </span>
                          <span className="text-sm bg-green-100 text-green-800 px-2 py-0.5 rounded">
                            {creneau.heure_debut} - {creneau.heure_fin}
                          </span>
                        </div>
                        {creneau.description && (
                          <p className="text-sm text-gray-500 mt-1">{creneau.description}</p>
                        )}
                      </div>
                      {creneauPrefere === creneau.code_creneau && (
                        <CheckCircle className="h-5 w-5 text-green-600 ml-2" />
                      )}
                    </label>
                  ))}
                </div>

                {/* Message créneau */}
                {creneauMessage && (
                  <div className={`p-3 rounded-lg mb-4 flex items-center gap-2 text-sm ${
                    creneauMessage.type === 'success'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {creneauMessage.type === 'success' ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <AlertCircle className="h-4 w-4" />
                    )}
                    {creneauMessage.text}
                  </div>
                )}

                {/* Bouton enregistrer créneau */}
                <button
                  onClick={saveCreneauPrefere}
                  disabled={savingCreneau || !creneauPrefere || creneauPrefere === user?.creneau_retrait_prefere}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                    savingCreneau || !creneauPrefere || creneauPrefere === user?.creneau_retrait_prefere
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {savingCreneau ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Enregistrer ma préférence
                    </>
                  )}
                </button>
              </>
            )}
          </div>

          {/* ═══════════════════════════════════════════════════════════════════ */}
          {/* SECTION MODIFICATION MOT DE PASSE */}
          {/* ═══════════════════════════════════════════════════════════════════ */}
          <div className="border-t pt-6 mt-6">
            <button
              onClick={() => setShowPasswordSection(!showPasswordSection)}
              className="flex items-center gap-2 text-gray-700 hover:text-green-600 transition-colors"
            >
              <Lock className="h-5 w-5" />
              <span className="font-semibold">Modifier mon mot de passe</span>
              {showPasswordSection ? (
                <ChevronUp className="h-4 w-4 ml-2" />
              ) : (
                <ChevronDown className="h-4 w-4 ml-2" />
              )}
            </button>

            {showPasswordSection && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="space-y-4 max-w-md">
                  {/* Nouveau mot de passe */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nouveau mot de passe
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.new ? 'text' : 'password'}
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg pr-10 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="Minimum 4 caractères"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('new')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPasswords.new ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Confirmer mot de passe */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirmer le mot de passe
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.confirm ? 'text' : 'password'}
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg pr-10 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="Retapez le mot de passe"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('confirm')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPasswords.confirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Message mot de passe */}
                  {passwordMessage && (
                    <div className={`p-3 rounded-lg flex items-center gap-2 text-sm ${
                      passwordMessage.type === 'success'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {passwordMessage.type === 'success' ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : (
                        <AlertCircle className="h-4 w-4" />
                      )}
                      {passwordMessage.text}
                    </div>
                  )}

                  {/* Boutons mot de passe */}
                  <div className="flex gap-3">
                    <button
                      onClick={handleChangePassword}
                      disabled={savingPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                        savingPassword || !passwordData.newPassword || !passwordData.confirmPassword
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      {savingPassword ? (
                        <>
                          <RefreshCw className="h-4 w-4 animate-spin" />
                          Modification...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4" />
                          Enregistrer
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowPasswordSection(false);
                        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                        setPasswordMessage(null);
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* RÉSERVATIONS EN COURS */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b bg-green-50">
            <h3 className="text-lg font-semibold text-green-800 flex items-center gap-2">
              <ShoppingBag className="h-5 w-5" />
              Mes réservations en cours
              <span className="bg-green-600 text-white text-sm px-2 py-0.5 rounded-full ml-2">
                {resasEnCours.length}
              </span>
            </h3>
          </div>
          
          <div className="divide-y">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                <p className="text-gray-500 mt-2">Chargement...</p>
              </div>
            ) : resasEnCours.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <ShoppingBag className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p>Aucune réservation en cours</p>
                <button
                  onClick={() => navigate('/catalog')}
                  className="mt-4 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                >
                  Faire une réservation
                </button>
              </div>
            ) : (
              resasEnCours.map(resa => {
                const statutStyle = getStatutStyle(resa.statut);
                const isExpanded = expandedResa === resa.id;
                const isEditing = editingResa === resa.id;
                const lignes = isEditing ? editingLignes : (lignesResa[resa.id] || []);
                const canEdit = ['en_cours'].includes(resa.statut);
                
                return (
                  <div key={resa.id} className="p-4">
                    {/* En-tête réservation */}
                    <div 
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => toggleDetails(resa.id)}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                            {resa.numero_reservation}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-full ${statutStyle.bg} ${statutStyle.text}`}>
                            {statutStyle.label}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 flex items-center gap-4">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {resa.periode_nom || `Période ${resa.periode_id}`}
                          </span>
                          <span className="flex items-center gap-1">
                            <Euro className="h-4 w-4" />
                            {Number(resa.montant_estime || 0).toFixed(2)}€
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{statutStyle.description}</p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {canEdit && !isEditing && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              startEditing(resa);
                            }}
                            className="text-blue-600 hover:text-blue-800 p-2"
                            title="Modifier"
                          >
                            <Edit className="h-5 w-5" />
                          </button>
                        )}
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    </div>

                    {/* Détails expandés */}
                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t">
                        {/* Boutons d'action (mode normal) */}
                        {!isEditing && canEdit && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            <button
                              onClick={() => startEditing(resa)}
                              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                            >
                              <Edit className="h-4 w-4" />
                              Modifier les quantités
                            </button>
                            <button
                              onClick={() => continuerAuCatalogue(resa.id)}
                              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                            >
                              <Plus className="h-4 w-4" />
                              Ajouter des produits
                            </button>
                            <button
                              onClick={() => annulerReservation(resa.id, resa.numero_reservation)}
                              className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                              Annuler la réservation
                            </button>
                          </div>
                        )}

                        {/* Boutons mode édition */}
                        {isEditing && (
                          <div className="flex gap-2 mb-4">
                            <button
                              onClick={saveModifications}
                              disabled={saving}
                              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
                            >
                              {saving ? (
                                <RefreshCw className="h-4 w-4 animate-spin" />
                              ) : (
                                <Send className="h-4 w-4" />
                              )}
                              {saving ? 'Sauvegarde...' : 'Enregistrer les modifications'}
                            </button>
                            <button
                              onClick={cancelEditing}
                              disabled={saving}
                              className="flex items-center gap-2 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
                            >
                              <ArrowLeft className="h-4 w-4" />
                              Annuler les modifications
                            </button>
                          </div>
                        )}

                        <h4 className="text-sm font-semibold text-gray-700 mb-2">
                          {isEditing ? '✏️ Modifier les produits' : 'Détail des produits'}
                        </h4>
                        
                        {lignes.length === 0 ? (
                          <p className="text-gray-500 text-sm">Aucun produit</p>
                        ) : (
                          <div className="space-y-2">
                            {lignes.map(ligne => (
                              <div 
                                key={ligne.id} 
                                className={`flex items-center justify-between p-3 rounded-lg ${
                                  isEditing ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'
                                }`}
                              >
                                <div className="flex items-center gap-3 flex-1">
                                  <Package className="h-4 w-4 text-gray-400" />
                                  <div>
                                    <span className="font-medium">{ligne.designation_produit}</span>
                                    <span className="text-gray-500 ml-2 text-sm">
                                      {ligne.unite}
                                    </span>
                                  </div>
                                </div>
                                
                                {isEditing ? (
                                  <div className="flex items-center gap-3">
                                    <button
                                      onClick={() => updateQuantite(ligne.id, -1)}
                                      className="bg-red-500 text-white w-8 h-8 rounded-full hover:bg-red-600 flex items-center justify-center"
                                    >
                                      <Minus className="h-4 w-4" />
                                    </button>
                                    <span className="font-bold text-lg min-w-[3rem] text-center">
                                      {ligne.quantite_reservee}
                                    </span>
                                    <button
                                      onClick={() => updateQuantite(ligne.id, 1)}
                                      className="bg-green-500 text-white w-8 h-8 rounded-full hover:bg-green-600 flex items-center justify-center"
                                    >
                                      <Plus className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => removeLigne(ligne.id)}
                                      className="ml-2 text-red-600 hover:text-red-800"
                                      title="Supprimer"
                                    >
                                      <Trash2 className="h-5 w-5" />
                                    </button>
                                    <span className="font-semibold text-green-600 min-w-[4rem] text-right">
                                      {(ligne.quantite_reservee * parseFloat(ligne.prix_unitaire)).toFixed(2)}€
                                    </span>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-4">
                                    <span className="text-gray-600">
                                      × {ligne.quantite_reservee}
                                    </span>
                                    <span className="font-semibold text-green-600">
                                      {Number(ligne.montant_estime || 0).toFixed(2)}€
                                    </span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Total en mode édition */}
                        {isEditing && (
                          <div className="mt-4 p-4 bg-green-100 rounded-lg flex justify-between items-center">
                            <span className="font-bold text-green-800">Nouveau total :</span>
                            <span className="text-2xl font-bold text-green-600">
                              {calculateTotal().toFixed(2)}€
                            </span>
                          </div>
                        )}
                        
                        {/* Infos dates */}
                        {!isEditing && (
                          <div className="mt-4 text-xs text-gray-500 space-y-1">
                            <p>📅 Demande transmise le {formatDate(resa.date_reservation)}</p>
                            {resa.date_confirmation && (
                              <p>✅ Confirmée par le fermier le {formatDate(resa.date_confirmation)}</p>
                            )}
                            {resa.date_retrait_prevue && (
                              <p>🚚 Retrait prévu le {formatDateShort(resa.date_retrait_prevue)}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Historique (réservations passées) */}
        {resasPassees.length > 0 && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Historique
                <span className="bg-gray-400 text-white text-sm px-2 py-0.5 rounded-full ml-2">
                  {resasPassees.length}
                </span>
              </h3>
            </div>
            
            <div className="divide-y">
              {resasPassees.map(resa => {
                const statutStyle = getStatutStyle(resa.statut);
                
                return (
                  <div key={resa.id} className="p-4 opacity-75">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <span className="font-mono text-sm text-gray-500">
                            {resa.numero_reservation}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-full ${statutStyle.bg} ${statutStyle.text}`}>
                            {statutStyle.label}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatDateShort(resa.date_reservation)} • {resa.periode_nom || `Période ${resa.periode_id}`}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-gray-600">
                          {Number(resa.montant_reel || resa.montant_estime || 0).toFixed(2)}€
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ProfileModule;